// haushalt.ts — Regeln für den Abschnitt „Haushalt von <Name>" unter der
// Kontaktsuche (User 28.09.2026): Trifft die Suche eine Person, stehen darunter
// ihre Haushaltsmitglieder — das Gegenstück zum Unternehmensverbund einer
// Firma (`unternehmensverbund.ts`, gleiche Erkennung über `trefferKandidat`).
//
// Bewusst ohne React, damit die Regeln per Vitest prüfbar sind. Die Daten
// kommen aus GET /contact-relation/haushalt/:id — das Backend liest
// `sameHousehold` in beide Richtungen und filtert nach Leserechten.

import { reverseKinshipLabel } from "../../../services/haushalteCleanupService";
import { genderOf } from "../../../utils/householdRole";

export type HaushaltsMitglied = {
  id: number;
  name: string | null;
  /** Was das Mitglied für die gesuchte Person ist — fertig vom Backend. */
  beziehung: string | null;
  /** Label aus der Gegenrichtung ohne gespeicherte Umkehr — hier umzukehren. */
  umzukehren: string | null;
  /** Nur über ein anderes Mitglied im Haushalt, ohne eigene Beziehung. */
  mittelbar: boolean;
  geburtsdatum: string | null;
  gender: string | null;
  anrede: string | null;
  status: string | null;
  verstorben: boolean;
};

export type Haushalt = {
  person: { id: number; name: string | null } | null;
  mitglieder: HaushaltsMitglied[];
};

/**
 * Was das Mitglied für die gesuchte Person ist. Die Umkehr läuft exakt wie in
 * der Kontaktakte (`ContactConnections.mkRel`): gespeicherte Gegenrichtung vor
 * berechneter, berechnet mit dem Geschlecht des Mitglieds; unbekannte Labels
 * (Freitext) bleiben, wie sie sind.
 */
export function beziehungDesMitglieds(m: HaushaltsMitglied): string {
  if (m.beziehung) return m.beziehung;
  if (m.umzukehren) {
    const g = genderOf({ gender: m.gender, salutationLabel: m.anrede });
    return reverseKinshipLabel(m.umzukehren, g, { partnerFallback: "opposite" }) || m.umzukehren;
  }
  return m.mittelbar ? "im Haushalt" : "";
}

/** Alter in vollen Jahren am Stichtag; `null` ohne (gültiges) Geburtsdatum. */
export function alterAm(geburtsdatum: string | null, heute: Date = new Date()): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(geburtsdatum ?? "");
  if (!m) return null;
  const [j, mo, t] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let alter = heute.getFullYear() - j;
  const vorGeburtstag = heute.getMonth() + 1 < mo || (heute.getMonth() + 1 === mo && heute.getDate() < t);
  if (vorGeburtstag) alter -= 1;
  return alter >= 0 && alter < 150 ? alter : null;
}

/** „Jg. 1982 · 43 J." — bei Verstorbenen nur der Jahrgang mit Kreuz. */
export function lebensdatenText(m: HaushaltsMitglied, heute: Date = new Date()): string {
  const jahr = /^(\d{4})/.exec(m.geburtsdatum ?? "")?.[1];
  if (m.verstorben) return jahr ? `Jg. ${jahr} · †` : "†";
  if (!jahr) return "";
  const alter = alterAm(m.geburtsdatum, heute);
  return alter == null ? `Jg. ${jahr}` : `Jg. ${jahr} · ${alter} J.`;
}

/** Info-Zeile eines Mitglieds: Beziehung, Lebensdaten, Status. */
export function mitgliedInfo(m: HaushaltsMitglied, heute: Date = new Date()): string {
  return [beziehungDesMitglieds(m), lebensdatenText(m, heute), m.status ?? ""]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" · ");
}

/** Gibt es etwas zu zeigen? Eine Person ohne Haushalt bleibt ohne Abschnitt. */
export function hatHaushalt(h: Haushalt | null): h is Haushalt {
  return !!h?.person && h.mitglieder.length > 0;
}
